import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { MultiSelect } from './MultiSelect';
import { SidebarUserMenu } from './SidebarUserMenu';
import { navSections, type NavChildItem } from '../config/navigation';
import { getAllowedSiteSlugsForBrand, hasAnyAdSitePermissionCode } from '../lib/adPermissions';
import { trackMenuClick } from '../lib/usageTracking';

function formatSiteCode(site: string): string {
  const raw = String(site || '').trim();
  if (!raw) return '';
  const code = raw.split('-')[0] || raw;
  return code.toUpperCase();
}

const marketingMenuAvailability: Record<string, { brands?: string[]; regions?: string[] }> = {
  "/ad-analysis/sku-roi": { brands: ["zeelool"], regions: ["us"] },
  "/ad-analysis/channel-wide-table": { brands: ["zeelool"], regions: ["us"] },
  "/ad-analysis/acquisition-cost": { brands: ["vooglam"], regions: ["us"] },
  "/ad-analysis/ad-dashboard/google": { brands: ["zeelool", "vooglam"], regions: ["us", "fr", "de", "ca", "uk"] },
  "/ad-analysis/ad-dashboard/meta": { brands: ["zeelool", "vooglam"], regions: ["us", "fr", "de", "ca", "uk"] },
  "/ad-analysis/ad-dashboard/tiktok": { brands: ["zeelool"], regions: ["us", "de"] },
  "/ad-analysis/ad-dashboard/snapchat": { brands: ["zeelool", "vooglam"], regions: ["us"] },
  "/ad-analysis/ad-dashboard/bing": { brands: ["zeelool", "vooglam"], regions: ["us", "ca", "uk", "de"] },
  "/ad-analysis/ad-dashboard/criteo": { brands: ["zeelool"], regions: ["us", "ca", "de"] },
  "/ad-analysis/ad-dashboard/applovin": { brands: ["zeelool"], regions: ["us"] },
  "/ad-analysis/ad-dashboard/rtb": { brands: ["zeelool", "vooglam"], regions: ["us"] },
};

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

interface SidebarProps {
  mobileOpen?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const {
    sidebarCollapsed,
    setBrand,
    toggleSidebar,
    brand,
    site,
    brands,
    sites,
    brandId,
    siteId,
    fetchBrands,
    fetchSites,
    selectBrandById,
    selectSiteById,
    hasPermission,
    adContext,
    permissionCodes,
    permissionsLoaded,
  } = useAppStore();

  const brandOptions = useMemo(() => {
    const sortedBrands = brandId
      ? [...brands].sort((a, b) => (a.id === brandId ? -1 : b.id === brandId ? 1 : 0))
      : brands;

    if (permissionsLoaded && sortedBrands.length > 0) {
      const hasBrandCodes = hasAnyAdSitePermissionCode(permissionCodes);
      if (hasBrandCodes) {
        return sortedBrands.map((b) => ({
          value: b.id,
          label: b.name,
          disabled: !permissionCodes.some((c) => c.toLowerCase().startsWith(`ad:${b.name.toLowerCase()}:`)),
        }));
      }
    }

    if (permissionsLoaded && permissionCodes.length > 0) {
      return sortedBrands.map((b) => ({ value: b.id, label: b.name, disabled: true }));
    }

    const allowedSet = adContext?.brand_access?.length ? new Set(adContext.brand_access) : null;
    return sortedBrands.map((b) => ({
      value: b.id,
      label: b.name,
      disabled: allowedSet ? !allowedSet.has(b.id) : false,
    }));
  }, [brands, brandId, adContext, permissionCodes, permissionsLoaded]);

  const isVisibleForBrand = (item: { hiddenForBrands?: Array<"zeelool" | "vooglam"> }) =>
    !item.hiddenForBrands?.includes(brand);

  const currentSiteRegion = useMemo(() => {
    const currentSite = sites.find(item => item.id === siteId);
    return formatSiteCode(currentSite?.code || site).toLowerCase();
  }, [site, siteId, sites]);

  const isVisibleForBrandRegion = (item: NavChildItem) =>
    !item.hiddenForBrandRegions?.[brand]?.includes(currentSiteRegion);

  const isVisibleForAvailability = (item: NavChildItem) => {
    const availability = marketingMenuAvailability[item.path];
    if (!availability) return true;
    if (availability.brands && !availability.brands.includes(brand)) return false;
    if (availability.regions && !availability.regions.includes(currentSiteRegion)) return false;
    return true;
  };

  const filterVisibleChildren = (children: NavChildItem[] = []): NavChildItem[] =>
    children
      .map((child) => {
        if (!child.children) return child;
        return { ...child, children: filterVisibleChildren(child.children) };
      })
      .filter((child) => {
        if (child.hidden) return false;
        if (child.hiddenInClassic) return false;
        if (!isVisibleForBrand(child)) return false;
        if (!isVisibleForBrandRegion(child)) return false;
        if (!isVisibleForAvailability(child)) return false;
        if (child.permCode && !hasPermission(child.permCode)) return false;
        if (child.children && child.children.length === 0) return false;
        return true;
      });

  const isChildActive = (child: NavChildItem): boolean =>
    location.pathname === child.path ||
    location.pathname.startsWith(child.path + '/') ||
    (child.children ?? []).some(isChildActive);

  const firstChildPath = (children: NavChildItem[] = []): string | null => {
    for (const child of children) {
      if (child.children?.length) {
        const nested = firstChildPath(child.children);
        if (nested) return nested;
      }
      if (child.path) return child.path;
    }
    return null;
  };

  const childExpandKey = (child: NavChildItem, depth: number) =>
    `child:${depth}:${child.path}:${child.label}`;

  const collectActiveChildExpandKeys = (children: NavChildItem[], depth = 0): Record<string, boolean> => {
    const keys: Record<string, boolean> = {};
    for (const child of children) {
      if (!child.children?.length) continue;
      if (isChildActive(child)) {
        keys[childExpandKey(child, depth)] = true;
        Object.assign(keys, collectActiveChildExpandKeys(child.children, depth + 1));
      }
    }
    return keys;
  };

  const renderChildItems = (children: NavChildItem[], depth = 0): React.ReactNode => (
    children.map((child) => {
      const active = isChildActive(child);
      if (child.children?.length) {
        const expandKey = childExpandKey(child, depth);
        const isExpanded = !!expandedMenus[expandKey];
        return (
          <React.Fragment key={child.path}>
            <div
              className={`nav-item ${active ? 'active' : ''}`}
              style={{
                fontSize: 12.5,
                padding: '5px 12px',
                minHeight: 30,
                cursor: 'pointer',
                opacity: 1,
                marginLeft: depth * 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onClick={() => setExpandedMenus((prev) => ({ ...prev, [expandKey]: !prev[expandKey] }))}
            >
              <span className="nav-text">{child.label}</span>
              <span
                style={{
                  transition: 'transform .2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                  opacity: 0.5,
                }}
              >
                <ChevronDownIcon />
              </span>
            </div>
            {isExpanded && (
              <div style={{ paddingLeft: 12 }}>
                {renderChildItems(child.children, depth + 1)}
              </div>
            )}
          </React.Fragment>
        );
      }
      return (
        <NavLink
          key={child.path}
          to={child.path}
          className={`nav-item ${active ? 'active' : ''}`}
          style={{
            fontSize: 12.5,
            padding: '5px 12px',
            minHeight: 30,
            cursor: 'pointer',
            opacity: 1,
            marginLeft: depth * 12,
          }}
          onClick={() => trackMenuClick(child.path, child.label, location.pathname)}
        >
          <span className="nav-text">{child.label}</span>
        </NavLink>
      );
    })
  );

  const allowedSiteIds = useMemo((): Set<string> | null => {
    if (!brandId) return null;

    const brandName = brands.find((b) => b.id === brandId)?.name?.toLowerCase();
    if (permissionsLoaded && brandName && sites.length > 0) {
      const hasBrandCodes = hasAnyAdSitePermissionCode(permissionCodes);
      if (hasBrandCodes) {
        const slugs = getAllowedSiteSlugsForBrand(permissionCodes, brandName);

        if (slugs.includes('global')) return null;
        if (slugs.length === 0) return new Set<string>();

        const allowedSiteCodes = new Set(slugs.map((s) => s.toLowerCase()));
        return new Set(
          sites
            .filter((s) => {
              const slug = (s.code?.split('-')[0] || s.code)?.toLowerCase();
              return slug ? allowedSiteCodes.has(slug) : false;
            })
            .map((s) => s.id),
        );
      }
    }

    if (permissionsLoaded && permissionCodes.length > 0) {
      return new Set<string>();
    }

    return null;
  }, [brandId, brands, sites, permissionCodes, permissionsLoaded]);

  useEffect(() => {
    if (!permissionsLoaded || brandOptions.length === 0) return;
    const hasBrandCodes = hasAnyAdSitePermissionCode(permissionCodes);
    if (!hasBrandCodes) return;

    const currentOption = brandOptions.find((b) => b.value === brandId);
    if (currentOption?.disabled) {
      const first = brandOptions.find((b) => !b.disabled);
      if (first) selectBrandById(first.value);
    }
  }, [brandOptions, brandId, permissionCodes, permissionsLoaded, selectBrandById]);

  useEffect(() => {
    if (!allowedSiteIds || !siteId) return;
    if (!allowedSiteIds.has(siteId)) {
      const firstAllowed = sites.find((s) => allowedSiteIds.has(s.id));
      if (firstAllowed) selectSiteById(firstAllowed.id);
    }
  }, [allowedSiteIds, siteId, sites, selectSiteById]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  useEffect(() => {
    if (brandId) {
      fetchSites(brandId);
    }
  }, [brandId, fetchSites]);

  useEffect(() => {
    setExpandedMenus((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const section of navSections) {
        for (const item of section.items) {
          const visibleChildren = filterVisibleChildren(item.children ?? []);
          if (visibleChildren.length && visibleChildren.some(isChildActive)) {
            if (!next[item.path]) {
              next[item.path] = true;
              changed = true;
            }
            const childKeys = collectActiveChildExpandKeys(visibleChildren);
            for (const key of Object.keys(childKeys)) {
              if (!next[key]) {
                next[key] = true;
                changed = true;
              }
            }
          }
        }
      }

      return changed ? next : prev;
    });
  }, [location.pathname, brand, currentSiteRegion]);

  const userInfo = useMemo(() => {
    try {
      const stored = localStorage.getItem('user_info');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore
    }
    return null;
  }, []);

  const userName = userInfo?.name || 'User';

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBrandId = e.target.value;
    const brandItem = brands.find((b) => b.id === selectedBrandId);
    if (brandItem) {
      selectBrandById(selectedBrandId);
    } else {
      const newBrand = selectedBrandId as 'zeelool' | 'vooglam';
      setBrand(newBrand);
    }
  };

  const handleSiteClick = (siteItemId: string) => {
    selectSiteById(siteItemId);
  };

  return (
    <>
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>
        <div className="logo-area">
          <div
            style={{
              width: 34,
              height: 34,
              flexShrink: 0,
            }}
            title="鹿鸣 · 智能广告全域增长平台"
          >
            <img src="/icons/logo.svg" alt="" width={34} height={34} style={{ display: 'block' }} />
          </div>
          {!sidebarCollapsed && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text)', lineHeight: 1.25 }}>
                鹿鸣
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text3)', marginLeft: 2 }}>
                  · 全域增长广告智能体
                </span>
              </div>
              <div
                style={{
                  fontSize: 8.5,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--text3)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  marginTop: 2,
                }}
              >
                {'OMNI GROWTH AD\u00A0\u00A0AI AGENT'}
              </div>
            </div>
          )}
        </div>

        {!sidebarCollapsed && (
          <div className="brand-switcher">
            <span className="brand-label">品牌 / Brand</span>
            <MultiSelect
              single
              options={
                brandOptions.length > 0
                  ? brandOptions
                  : [
                      { value: 'zeelool', label: 'Zeelool' },
                      { value: 'vooglam', label: 'Vooglam' },
                    ]
              }
              value={brandId ? [brandId] : []}
              onChange={(vals) => {
                if (vals[0]) {
                  handleBrandChange({ target: { value: vals[0] } } as React.ChangeEvent<HTMLSelectElement>);
                }
              }}
              style={{ width: '100%' }}
            />
            <div className="site-chips">
              {sites.length > 0 ? (
                (() => {
                  const allLocked = allowedSiteIds !== null && allowedSiteIds.size === 0;
                  if (allLocked) {
                    return (
                      <>
                        {sites.map((s) => (
                          <button
                            key={s.id}
                            className="site-chip locked"
                            title="无该站点的访问权限"
                            style={{ opacity: 0.35, cursor: 'not-allowed' }}
                          >
                            {formatSiteCode(s.code)}
                            <span style={{ marginLeft: 3, fontSize: 10 }}>🔒</span>
                          </button>
                        ))}
                      </>
                    );
                  }

                  return sites.map((s) => {
                    const isAllowed = !allowedSiteIds || allowedSiteIds.has(s.id);
                    return (
                      <button
                        key={s.id}
                        className={`site-chip ${siteId === s.id ? 'active' : ''} ${!isAllowed ? 'locked' : ''}`}
                        onClick={() => (isAllowed ? handleSiteClick(s.id) : undefined)}
                        title={!isAllowed ? '无该站点的访问权限' : undefined}
                        style={!isAllowed ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                      >
                        {formatSiteCode(s.code)}
                        {!isAllowed && <span style={{ marginLeft: 3, fontSize: 10 }}>🔒</span>}
                      </button>
                    );
                  });
                })()
              ) : (
                <span style={{ fontSize: 12, opacity: 0.5 }}>Loading...</span>
              )}
            </div>
          </div>
        )}

        <nav className="nav">
          <>
            {navSections.map((section) => {
              const visibleItems = section.items.filter(
                (item) => !item.hidden && !item.hiddenInClassic && isVisibleForBrand(item) && (!item.permCode || hasPermission(item.permCode)),
              );
              if (visibleItems.length === 0) return null;

              return (
                <React.Fragment key={section.title}>
                  {!sidebarCollapsed && <div className="nav-section">{section.title}</div>}
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const visibleChildren = filterVisibleChildren(item.children ?? []);
                    if (item.children && !sidebarCollapsed) {
                      const isExpanded = !!expandedMenus[item.path];
                      const childActive = visibleChildren.some(isChildActive);
                      return (
                        <React.Fragment key={item.path}>
                          <div
                            className={`nav-item ${childActive ? 'active' : ''}`}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            onClick={() => setExpandedMenus((prev) => ({ ...prev, [item.path]: !prev[item.path] }))}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Icon />
                              <span className="nav-text">{item.label}</span>
                            </span>
                            <span
                              style={{
                                transition: 'transform .2s',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                opacity: 0.5,
                              }}
                            >
                              <ChevronDownIcon />
                            </span>
                          </div>
                          {isExpanded && (
                            <div style={{ paddingLeft: 28 }}>
                              {renderChildItems(visibleChildren)}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    }

                    const isActive = item.children
                      ? visibleChildren.some(isChildActive)
                      : location.pathname === item.path;
                    const targetPath = item.children ? firstChildPath(visibleChildren) : item.path;
                    if (!targetPath) return null;

                    return (
                      <NavLink
                        key={item.path}
                        to={targetPath}
                        className={`nav-item ${isActive ? 'active' : ''}`}
                        data-label={item.label}
                        title={sidebarCollapsed ? item.label : undefined}
                        onClick={() => trackMenuClick(targetPath, item.label, location.pathname)}
                      >
                        <Icon />
                        {!sidebarCollapsed && <span className="nav-text">{item.label}</span>}
                      </NavLink>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </>
        </nav>

        <button className="collapse-toggle" onClick={toggleSidebar}>
          <ChevronLeftIcon />
          {!sidebarCollapsed && <span className="toggle-text">收起菜单</span>}
        </button>

        <div className="sidebar-footer">
          <SidebarUserMenu userName={userName} collapsed={sidebarCollapsed} mobileOpen={mobileOpen} />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
